import type { Cotizacion } from "./types";

type Objeto = Record<string, unknown>;
const objeto = (v: unknown): v is Objeto =>
  !!v && typeof v === "object" && !Array.isArray(v);
const textos = (v: Objeto, keys: string[]) =>
  keys.every((k) => typeof v[k] === "string");
const numeros = (v: Objeto, keys: string[]) =>
  keys.every((k) => typeof v[k] === "number" && Number.isFinite(v[k]));
const opcionales = (v: Objeto, keys: string[]) =>
  keys.every(
    (k) => v[k] === null || (typeof v[k] === "number" && Number.isFinite(v[k])),
  );
const lista = (v: unknown, comprobar: (v: Objeto) => boolean): boolean =>
  Array.isArray(v) && v.every((x) => objeto(x) && comprobar(x));
const cadenas = (v: unknown) =>
  Array.isArray(v) && v.every((x) => typeof x === "string");

const esGarantia = (g: Objeto) =>
  textos(g, ["id", "categoria", "notas"]) &&
  opcionales(g, ["anios"]) &&
  typeof g.incluida === "boolean";

// Nunca introducir un snapshot externo sin validar en el estado de React.
// Los borradores incompletos son válidos; aquí se valida estructura, no negocio.
export function esSnapshot(v: unknown): v is Cotizacion {
  if (!objeto(v)) return false;
  return (
    textos(v, [
      "numero",
      "formatoPdf",
      "simulacionUrl",
      "fachadaUrl",
      "correoEnvio",
      "telefonoEnvio",
    ]) &&
    ["pdf", "whatsapp", "correo", "ambos"].includes(String(v.canal)) &&
    (v.registroId === null || typeof v.registroId === "string") &&
    ["etiquetaPropuesta", "numeroBase"].every(
      (k) => v[k] === undefined || typeof v[k] === "string",
    ) &&
    ["numeroOficial", "numeroBaseOficial"].every(
      (k) => v[k] == null || typeof v[k] === "string",
    ) &&
    opcionales(v, ["revision"]) &&
    numeros(v, ["margen", "descuento", "ivaPct"]) &&
    ["margenActivo", "ivaIncluido", "incluirGarantias"].every(
      (k) => typeof v[k] === "boolean",
    ) &&
    (v.mostrarValorUnitario === undefined ||
      typeof v.mostrarValorUnitario === "boolean") &&
    objeto(v.cliente) &&
    textos(v.cliente, [
      "nombre",
      "encargado",
      "nombre_proyecto",
      "direccion_facturacion",
      "cliente_contrato",
    ]) &&
    objeto(v.condiciones) &&
    textos(v.condiciones, ["forma_pago", "tiempo_entrega", "validez_oferta"]) &&
    (v.contacto === null ||
      (objeto(v.contacto) && textos(v.contacto, ["id", "nombre"]))) &&
    lista(v.garantias, esGarantia) &&
    lista(
      v.sitios,
      (s) =>
        textos(s, ["id", "nombre", "direccion", "ciudad", "sede"]) &&
        opcionales(s, ["margenOverride"]) &&
        // Garantías por sitio y por pieza: snapshots anteriores no las traen.
        (s.garantias === undefined || lista(s.garantias, esGarantia)) &&
        objeto(s.logistica) &&
        typeof s.logistica.incluida === "boolean" &&
        numeros(s.logistica, ["visitas", "costoPorVisita"]) &&
        opcionales(s.logistica, ["totalManual"]) &&
        (s.ciudadPersonalizada === undefined ||
          (objeto(s.ciudadPersonalizada) &&
            textos(s.ciudadPersonalizada, ["ciudad"]) &&
            numeros(s.ciudadPersonalizada, ["costoFijo"]))) &&
        lista(
          s.addons,
          (a) =>
            textos(a, ["id", "nombre"]) &&
            numeros(a, ["costo"]) &&
            (a.cantidades === null ||
              (objeto(a.cantidades) &&
                Object.values(a.cantidades).every(
                  (n) => typeof n === "number" && Number.isFinite(n),
                ))),
        ) &&
        lista(
          s.productos,
          (pr) =>
            textos(pr, ["id", "nombre"]) &&
            (pr.columnaResumen === undefined ||
              typeof pr.columnaResumen === "string") &&
            cadenas(pr.imagenes) &&
            lista(
              pr.piezas,
              (p) =>
                textos(p, [
                  "id",
                  "familia",
                  "variante",
                  "material",
                  "iluminacion",
                ]) &&
                ["m2", "cm2", "ml", "cm_lineal", "unidad"].includes(
                  String(p.unidad),
                ) &&
                numeros(p, [
                  "anchoCm",
                  "altoCm",
                  "profundidadCm",
                  "cantidad",
                  "descuentoPct",
                ]) &&
                opcionales(p, [
                  "precioUnitario",
                  "precioContratado",
                  "precioAjustado",
                  "margenOverride",
                ]) &&
                // Campos nuevos: snapshots anteriores no los traen.
                (p.garantias === undefined ||
                  lista(p.garantias, esGarantia)) &&
                (p.precioAjustadoM2 == null ||
                  (typeof p.precioAjustadoM2 === "number" &&
                    Number.isFinite(p.precioAjustadoM2))) &&
                (p.areaM2 == null ||
                  (typeof p.areaM2 === "number" &&
                    Number.isFinite(p.areaM2))) &&
                (p.unidadMedida == null ||
                  ["m", "cm", "mm"].includes(String(p.unidadMedida))) &&
                (p.unidades == null ||
                  ["ancho", "alto", "profundidad"].every((k) =>
                    ["m", "cm", "mm"].includes(
                      String((p.unidades as Record<string, unknown>)[k]),
                    ),
                  )) &&
                (p.materiales == null ||
                  lista(
                    p.materiales,
                    (m) =>
                      textos(m, ["nombre", "unidad"]) &&
                      numeros(m, ["cantidad", "precioUnitario"]) &&
                      typeof m.porArea === "boolean",
                  )) &&
                (p.materialesReferencia == null ||
                  cadenas(p.materialesReferencia)) &&
                (p.catalogoEspecial == null ||
                  (objeto(p.catalogoEspecial) &&
                    textos(p.catalogoEspecial, [
                      "id",
                      "codigoArticulo",
                      "clienteMarca",
                    ]))),
            ),
        ),
    )
  );
}
